import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core';
import ArrowRightIcon from '@untitled-ui/icons-react/build/esm/ArrowRight';
import PlusIcon from '@untitled-ui/icons-react/build/esm/Plus';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';

import { useSettings } from 'src/hooks/use-settings';
import Layout from 'src/components/layout/Layout';

import { AnalyticsStats } from 'src/sections/dashboard/analytics/analytics-stats';
import { AnalyticsMostVisited } from 'src/sections/dashboard/analytics/analytics-most-visited';
import { AnalyticsSocialSources } from 'src/sections/dashboard/analytics/analytics-social-sources';
import { AnalyticsTrafficSources } from 'src/sections/dashboard/analytics/analytics-traffic-sources';
import { AnalyticsVisitsByCountry } from 'src/sections/dashboard/analytics/analytics-visits-by-country';
import { useDispatch } from 'react-redux';
import { getScreenStatsRankingGroupByMac } from 'src/redux/actions/ScreenStats';
import AnalyticsIntenetUsage from './components/AnalyticsIntenetUsage';
import { useSelector } from 'react-redux';
import {
  Card,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
} from '@mui/material';
import TvIcon from '@mui/icons-material/Tv';
import EditIcon from '@mui/icons-material/Edit';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Link } from 'react-router-dom';

const ReportsPage = ({ user }) => {
  const { t } = useTranslation();
  const settings = useSettings();
  const [latelyRegisteredUsers, setLatelyRegisteredUsers] = React.useState(7);
  const [maxDownloadsAlert, setMaxDownloadsAlert] = React.useState(1000);
  const [sumDownloadsAlert, setSumDownloadsAlert] = React.useState(50000);

  const { stats } = useSelector((state) => state.WidgetsGrids);

  // Sample data for file downloads
  const fileDownloadsRanking = [
    {
      id: 1,
      fileName: 'promo-video.mp4',
      user: 'admin@vcplayer.com',
      downloads: 1247,
    },
    {
      id: 2,
      fileName: 'product-catalog.pdf',
      user: 'karol@kokosza.com.pl',
      downloads: 982,
    },
    {
      id: 3,
      fileName: 'banner-sale.jpg',
      user: 'webappdev33@gmail.com',
      downloads: 876,
    }
  ];

  const averageDownloads = 34;
  const topFile = fileDownloadsRanking[0];

  return (
    <>
      <Layout>
        <Box
          component='main'
          sx={{
            flexGrow: 1,
            py: 2,
          }}
        >
          <Container maxWidth={settings.stretch ? false : 'xl'}>
            <Grid container spacing={4}>
              <Grid xs={6} md={3}>
                <Card
                  sx={{
                    p: 3,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                    }}
                  >
                    <TvIcon fontSize='large' color='primary' />
                    <Typography variant='h5'>Screens</Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      mt: 2,
                    }}
                  >
                    <Box>
                      <Typography variant='caption'>
                        {'Active screens'}
                      </Typography>
                      <Typography variant='h4' color={'primary.main'}>
                        {stats?.activeScreens || '0'}
                      </Typography>
                    </Box>
                    <Divider orientation='vertical' />
                    <Box>
                      <Typography variant='caption'>
                        {'Inactive screens'}
                      </Typography>
                      <Typography variant='h4' color={'text.secondary'}>
                        {stats?.inactiveScreens || '0'}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
              <Grid xs={6} md={3}>
                <Card
                  sx={{
                    p: 3,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                    }}
                  >
                    <TvIcon fontSize='large' color='primary' />
                    <Typography variant='h5'>Files & Widgets</Typography>
                    <Typography variant='h5' color={'primary.main'}>
                      {stats?.filesApps + stats?.apps}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      mt: 2,
                    }}
                  >
                    <Box>
                      <Typography variant='caption'>{'Files'}</Typography>
                      <Typography variant='h4' color={'primary.main'}>
                        {stats?.filesApps || '0'}
                      </Typography>
                    </Box>
                    <Divider orientation='vertical' />
                    <Box>
                      <Typography variant='caption'>{'Widgets'}</Typography>
                      <Typography variant='h4'>{stats?.apps || '0'}</Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
              <Grid xs={6} md={3}>
                <Card
                  sx={{
                    p: 3,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                    }}
                  >
                    <TvIcon fontSize='large' color='primary' />
                    <Typography variant='h5'>Users</Typography>
                    <Typography variant='h5' color={'primary.main'}>
                      {stats?.verifiedUsers + stats?.unverifiedUsers}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      mt: 2,
                    }}
                  >
                    <Box>
                      <Typography variant='caption'>{'Verified'}</Typography>
                      <Typography variant='h4' color={'primary.main'}>
                        {stats?.verifiedUsers || '0'}
                      </Typography>
                    </Box>
                    <Divider orientation='vertical' />
                    <Box>
                      <Typography variant='caption'>{'Unverified'}</Typography>
                      <Typography variant='h4'>
                        {stats?.unverifiedUsers || '0'}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
              <Grid xs={6} md={3}>
                <Card
                  sx={{
                    p: 3,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                    }}
                  >
                    <Typography variant='h5'>
                      Lately registered users
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      mt: 4,
                    }}
                  >
                    <Box>
                      <FormControl>
                        <InputLabel id='select-period-label'>
                          Select period
                        </InputLabel>
                        <Select
                          value={latelyRegisteredUsers}
                          onChange={(e) =>
                            setLatelyRegisteredUsers(e.target.value)
                          }
                          label='Select period'
                          labelId='select-period-label'
                        >
                          <MenuItem value={7}>Last 7 days</MenuItem>
                          <MenuItem value={14}>Last 14 days</MenuItem>
                          <MenuItem value={30}>Last 30 days</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Typography variant='h5' color={'primary.main'}>
                      {latelyRegisteredUsers == 14
                        ? stats?.userPer2Weeks
                        : latelyRegisteredUsers == 30
                        ? stats?.userPerMonth
                        : stats?.userPerWeek}
                    </Typography>
                    <Typography variant='h5'>accounts</Typography>
                  </Box>
                </Card>
              </Grid>
              <Grid xs={6} md={3}>
                <Card
                  sx={{
                    p: 3,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                    }}
                  >
                    <Typography variant='h5'>
                      Files request statistics
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      mt: 4,
                    }}
                  >
                    <Link to={'/requests-report' }>
                        <Button endIcon={<ChevronRightIcon />} variant="contained" >Go to</Button>
                    </Link>
                  </Box>
                </Card>
              </Grid>              
            </Grid>
          </Container>
        </Box>
      </Layout>
    </>
  );
};

export default ReportsPage;
